import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { QuestionEngine } from '@/systems/QuestionEngine';
import { ScoreSystem } from '@/systems/ScoreSystem';
import type { PerTableStats, Question } from '@/types';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { ComboMeter } from '@/ui/components/ComboMeter';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';
import type { TrainingConfig } from '@/scenes/TrainingPickScene';
import type { TrainingResultPayload } from '@/scenes/TrainingResultScene';

const FEEDBACK_DELAY_MS = 700;
const TIMER_BAR_WIDTH = 600;
const TIMER_BAR_HEIGHT = 14;

/**
 * TrainingScene — Treino Livre (CLAUDE.md §4.2).
 *
 * Diferente da Missão: sem vidas, sem fim previsível. A criança decide
 * quando parar tocando "Parar" e cai numa tela de sumário com os ganhos
 * da sessão. Erro não interrompe o fluxo — só reseta o combo e dá o
 * mesmo feedback positivo do modo principal.
 */
export class TrainingScene extends Phaser.Scene {
  private engine!: QuestionEngine;
  private scoreSys!: ScoreSystem;
  private currentQuestion?: Question;

  private config: TrainingConfig = { tables: [1], timeLimitMs: 6000, paceLabel: 'Normal' };

  private maxCombo = 0;
  private totalCorrectResponseTimeMs = 0;
  private perTable: Record<number, PerTableStats> = {};
  private sessionStartedAt = 0;
  private questionStartedAt = 0;

  // HUD
  private hudScore?: Phaser.GameObjects.Text;
  private hudAccuracy?: Phaser.GameObjects.Text;
  private hudTime?: Phaser.GameObjects.Text;
  private comboMeter?: ComboMeter;

  // Question UI
  private questionLabel?: Phaser.GameObjects.Text;
  private feedbackLabel?: Phaser.GameObjects.Text;
  private timerFill?: Phaser.GameObjects.Graphics;
  private timerEvent?: Phaser.Time.TimerEvent;
  private sessionTickEvent?: Phaser.Time.TimerEvent;

  private answerButtons: Button[] = [];
  private acceptingInput = false;

  constructor() {
    super({ key: 'TrainingScene' });
  }

  init(data?: TrainingConfig): void {
    if (data && data.tables.length > 0) this.config = data;
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    this.engine = new QuestionEngine({
      level: 1,
      tables: this.config.tables,
      timeLimitMs: this.config.timeLimitMs,
    });
    this.scoreSys = new ScoreSystem();
    this.maxCombo = 0;
    this.totalCorrectResponseTimeMs = 0;
    this.perTable = {};
    this.sessionStartedAt = this.time.now;

    this.buildHud();
    this.buildQuestionArea();
    this.buildStopButton();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cleanupTimer();
      this.sessionTickEvent?.remove(false);
    });

    this.sessionTickEvent = this.time.addEvent({
      delay: 200,
      loop: true,
      callback: () => this.refreshHud(),
    });

    this.nextQuestion();
  }

  private buildHud(): void {
    const { width } = this.scale;

    this.hudScore = this.add.text(32, 24, this.formatScore(), {
      fontFamily: Typography.fontFamily,
      fontSize: '28px',
      color: Palette.textPrimaryCss,
      fontStyle: 'bold',
    });

    this.hudAccuracy = this.add
      .text(width / 2, 32, this.formatAccuracy(), {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5, 0);

    this.hudTime = this.add
      .text(width - 32, 32, this.formatSessionTime(), {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.textMutedCss,
      })
      .setOrigin(1, 0);

    this.comboMeter = new ComboMeter(this, width - 96, 110);
  }

  private buildQuestionArea(): void {
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height * 0.18, this.formatHeader(), {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.questionLabel = this.add
      .text(width / 2, height * 0.32, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '96px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const barX = width / 2 - TIMER_BAR_WIDTH / 2;
    const barY = height * 0.42;
    const frame = this.add.graphics();
    frame.lineStyle(2, Palette.textMuted, 1);
    frame.strokeRoundedRect(barX - 2, barY - 2, TIMER_BAR_WIDTH + 4, TIMER_BAR_HEIGHT + 4, 6);

    this.timerFill = this.add.graphics();

    this.feedbackLabel = this.add
      .text(width / 2, height * 0.5, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private buildStopButton(): void {
    const { width, height } = this.scale;
    new Button(this, width - 110, height - 48, 'Parar', {
      width: 180,
      height: 48,
      fontSize: 20,
      fillColor: Palette.danger,
      hoverColor: Palette.warning,
      onClick: () => this.endSession(),
    });
  }

  private nextQuestion(): void {
    this.currentQuestion = this.engine.generate();
    this.feedbackLabel?.setText('');
    this.questionLabel?.setText(`${this.currentQuestion.factorA} × ${this.currentQuestion.factorB} = ?`);
    this.animateQuestionEntry();
    this.renderAnswerButtons(this.currentQuestion);
    this.startTimer();
    this.questionStartedAt = this.time.now;
    this.acceptingInput = true;
  }

  private renderAnswerButtons(question: Question): void {
    this.answerButtons.forEach((b) => b.destroy());
    this.answerButtons = [];

    const { width, height } = this.scale;
    const cols = 2;
    const buttonWidth = 220;
    const buttonHeight = 88;
    const gapX = 40;
    const gapY = 28;
    const totalWidth = cols * buttonWidth + (cols - 1) * gapX;
    const startX = width / 2 - totalWidth / 2 + buttonWidth / 2;
    const startY = height * 0.66;

    question.options.forEach((value, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (buttonWidth + gapX);
      const y = startY + row * (buttonHeight + gapY);

      const btn = new Button(this, x, y, String(value), {
        width: buttonWidth,
        height: buttonHeight,
        fontSize: 40,
        fillColor: Palette.primary,
        hoverColor: Palette.accent,
        cornerRadius: Layout.cornerRadius,
        onClick: () => this.onAnswer(value, index),
      });
      this.answerButtons.push(btn);
    });
  }

  private startTimer(): void {
    this.cleanupTimer();
    const limit = this.engine.getTimeLimitMs();
    this.drawTimer(1);
    this.timerEvent = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = this.time.now - this.questionStartedAt;
        const remaining = Math.max(0, 1 - elapsed / limit);
        this.drawTimer(remaining);
        if (remaining <= 0) {
          this.cleanupTimer();
          this.onTimeExpired();
        }
      },
    });
  }

  private drawTimer(ratio: number): void {
    if (!this.timerFill) return;
    const { width, height } = this.scale;
    const barX = width / 2 - TIMER_BAR_WIDTH / 2;
    const barY = height * 0.42;
    const color = ratio > 0.5 ? Palette.accent : ratio > 0.2 ? Palette.warning : Palette.danger;

    this.timerFill.clear();
    this.timerFill.fillStyle(color, 1);
    this.timerFill.fillRoundedRect(barX, barY, TIMER_BAR_WIDTH * ratio, TIMER_BAR_HEIGHT, 6);
  }

  private cleanupTimer(): void {
    this.timerEvent?.remove(false);
    this.timerEvent = undefined;
  }

  private onAnswer(value: number, buttonIndex: number): void {
    if (!this.acceptingInput || !this.currentQuestion) return;
    this.acceptingInput = false;
    this.cleanupTimer();
    this.drawTimer(0);

    const responseTime = this.time.now - this.questionStartedAt;
    const correct = value === this.currentQuestion.correctAnswer;
    this.engine.recordResult(this.currentQuestion, correct);

    const clickedButton = this.answerButtons[buttonIndex];

    if (correct) {
      const result = this.scoreSys.recordCorrect(responseTime, this.engine.getTimeLimitMs());
      this.maxCombo = Math.max(this.maxCombo, result.combo);
      this.totalCorrectResponseTimeMs += responseTime;
      const turbo = result.speedBonus ? ' · Turbo!' : '';
      this.feedbackLabel?.setColor(Palette.successCss).setText(`+${result.points}${turbo}`);
      audioManager.playSfx(result.speedBonus ? 'correctTurbo' : 'correct', { combo: result.combo });
      clickedButton?.flashCorrect();
      this.spawnScorePopup(clickedButton, result.points, result.speedBonus);
      if (result.combo > 0 && result.combo % 5 === 0) {
        audioManager.playSfx('combo', { combo: result.combo });
      }
    } else {
      this.scoreSys.recordWrong();
      this.feedbackLabel
        ?.setColor(Palette.warningCss)
        .setText(`Quase! A resposta era ${this.currentQuestion.correctAnswer}.`);
      audioManager.playSfx('wrong');
      clickedButton?.flashWrong();
      this.highlightCorrectButton();
    }

    this.trackPerTable(this.currentQuestion, correct);
    this.refreshHud();
    this.time.delayedCall(FEEDBACK_DELAY_MS, () => this.nextQuestion());
  }

  private onTimeExpired(): void {
    if (!this.acceptingInput || !this.currentQuestion) return;
    this.acceptingInput = false;

    this.engine.recordResult(this.currentQuestion, false);
    this.scoreSys.recordWrong();

    this.feedbackLabel
      ?.setColor(Palette.textMutedCss)
      .setText(`O tempo acabou! Era ${this.currentQuestion.correctAnswer}.`);
    audioManager.playSfx('timeUp');
    this.highlightCorrectButton();

    this.trackPerTable(this.currentQuestion, false);
    this.refreshHud();
    this.time.delayedCall(FEEDBACK_DELAY_MS, () => this.nextQuestion());
  }

  private animateQuestionEntry(): void {
    if (!this.questionLabel) return;
    this.questionLabel.setScale(0.85);
    this.tweens.add({
      targets: this.questionLabel,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  private highlightCorrectButton(): void {
    if (!this.currentQuestion) return;
    const correctIndex = this.currentQuestion.options.findIndex(
      (opt) => opt === this.currentQuestion!.correctAnswer,
    );
    if (correctIndex >= 0) this.answerButtons[correctIndex]?.highlightAsCorrect();
  }

  private spawnScorePopup(origin: Phaser.GameObjects.Container | undefined, points: number, turbo: boolean): void {
    if (!origin) return;
    const text = turbo ? `+${points} Turbo!` : `+${points}`;
    const popup = this.add
      .text(origin.x, origin.y - origin.height / 2, text, {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: turbo ? Palette.warningCss : Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: popup,
      y: popup.y - 60,
      alpha: { from: 1, to: 0 },
      duration: 700,
      ease: 'Cubic.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  private endSession(): void {
    this.cleanupTimer();
    this.answerButtons.forEach((b) => b.destroy());
    this.answerButtons = [];
    this.sessionTickEvent?.remove(false);

    const snap = this.scoreSys.snapshot();
    const payload: TrainingResultPayload = {
      score: snap.score,
      correctCount: snap.correctCount,
      wrongCount: snap.wrongCount,
      speedBonusRate: snap.speedBonusRate,
      maxCombo: this.maxCombo,
      totalCorrectResponseTimeMs: this.totalCorrectResponseTimeMs,
      perTable: this.perTable,
      durationMs: this.time.now - this.sessionStartedAt,
      tables: [...this.config.tables],
      paceLabel: this.config.paceLabel,
    };
    fadeToScene(this, 'TrainingResultScene', payload);
  }

  private trackPerTable(question: Question, correct: boolean): void {
    const inc = (table: number) => {
      const stats = this.perTable[table] ?? { questions: 0, correct: 0 };
      this.perTable[table] = {
        questions: stats.questions + 1,
        correct: stats.correct + (correct ? 1 : 0),
      };
    };
    inc(question.factorA);
    if (question.factorB !== question.factorA) inc(question.factorB);
  }

  private refreshHud(): void {
    this.hudScore?.setText(this.formatScore());
    this.hudAccuracy?.setText(this.formatAccuracy());
    this.hudTime?.setText(this.formatSessionTime());
    this.comboMeter?.update(this.scoreSys.getCombo(), this.scoreSys.getMultiplier());
  }

  private formatScore(): string {
    return `${this.scoreSys?.getScore() ?? 0} pts`;
  }

  private formatAccuracy(): string {
    const snap = this.scoreSys?.snapshot();
    const total = (snap?.correctCount ?? 0) + (snap?.wrongCount ?? 0);
    return `${snap?.correctCount ?? 0} / ${total} corretas`;
  }

  private formatSessionTime(): string {
    const elapsed = Math.max(0, this.time.now - this.sessionStartedAt);
    const totalSec = Math.floor(elapsed / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private formatHeader(): string {
    const tables = this.config.tables.length === 12 ? 'Todas' : this.config.tables.join(', ');
    return `Treino Livre · Tabuadas: ${tables} · Ritmo: ${this.config.paceLabel}`;
  }
}
